import type { OnInit } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  Input,
  inject,
} from '@angular/core';
import { Router } from '@angular/router';
import type { SearchItem } from '@freelancer/ui/search';
import { componentStoryMap } from '../../generated/component-story-map';
import { directiveStoryMap } from '../../generated/directive-story-map';
import { patternStoryMap } from '../../generated/patterns-story-map';
import { pipeStoryMap } from '../../generated/pipe-story-map';
import { prettyPrint } from '../../helpers';

interface StoryMap {
  [name: string]: {
    path: string;
    tags: readonly string[];
    internalOnly: boolean;
    stories: readonly string[];
  };
}

@Component({
  standalone: false,
  selector: 'app-search',
  template: `
    <fl-search
      [itemTypeTitles]="{
        info: 'Info',
        foundations: 'Foundations',
        components: 'Components',
        directives: 'Directives',
        pipes: 'Pipes',
        patterns: 'Patterns',
        motion: 'Motion System',
        brand: 'Brand Style Guide',
      }"
      [itemTemplates]="{
        info: searchItemTemplateRef,
        foundations: searchItemTemplateRef,
        components: searchItemTemplateRef,
        directives: searchItemTemplateRef,
        pipes: searchItemTemplateRef,
        patterns: searchItemTemplateRef,
        motion: searchItemTemplateRef,
        brand: searchItemTemplateRef,
      }"
      [scrollableSearchResults]="true"
      [searchResults]="searchResults"
      [displayResults]="displaySearchResults"
      [isExpandable]="isExpandable"
      [expandableIconLight]="expandableIconLight"
      (query)="handleQuery($event)"
      (select)="handleSelection($event)"
    >
      <ng-template
        #searchItemTemplateRef
        let-item
      >
        <p class="text-xxsmall">{{ item.name }}</p>
      </ng-template>
    </fl-search>
  `,
  styleUrls: ['./search.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SearchComponent implements OnInit {
  private router = inject(Router);

  displaySearchResults = true;
  searchResults: readonly SearchItem[] = [];
  searchItems: readonly SearchItem[];

  @Input() isExpandable = false;
  @Input() expandableIconLight = false;
  @Input() hideInternalPages = true;

  readonly internalOnlyStaticPages = ['how-to-contribute', 'report-bugs'];
  readonly infoPages: readonly string[] = [
    'introduction',
    'getting-started',
    'faq',
    'how-to-contribute',
    'report-bugs',
  ];
  readonly foundationPages: readonly string[] = [
    'accessibility',
    'colors',
    'grid',
    'spacing',
    'typography',
    'illustrations',
    'copywriting',
    'layouts',
    'forms',
    'error-handling',
  ];
  readonly motionPages: readonly string[] = [
    'getting-started',
    'attention-seeker',
    'fade',
    'slide',
  ];
  readonly brandPages: readonly string[] = ['brand-style-guide'];

  ngOnInit(): void {
    this.searchItems = [
      ...this.generateSearchItems(
        'info',
        this.getStaticPageList(this.infoPages),
      ),
      ...this.generateSearchItems(
        'foundations',
        this.getStaticPageList(this.foundationPages),
      ),
      ...this.generateSearchItems(
        'components',
        this.getStoryPageList(componentStoryMap, 'components'),
      ),
      ...this.generateSearchItems(
        'directives',
        this.getStoryPageList(directiveStoryMap, 'directives'),
      ),
      ...this.generateSearchItems(
        'pipes',
        this.getStoryPageList(pipeStoryMap, 'pipes'),
      ),
      ...this.generateSearchItems(
        'patterns',
        this.getStoryPageList(patternStoryMap, 'patterns'),
      ),
      ...this.generateSearchItems(
        'motion',
        this.getStaticPageList(this.motionPages),
      ),
      ...this.generateSearchItems(
        'brand',
        this.getStaticPageList(this.brandPages),
      ),
    ];
  }

  getStaticPageList(items: readonly string[]): readonly string[] {
    if (!this.hideInternalPages) {
      return items;
    }

    return items.filter(item => !this.internalOnlyStaticPages.includes(item));
  }

  getStoryPageList(items: StoryMap, type: string): readonly string[] {
    return Object.entries(items)
      .flatMap(([key, value]) => {
        // Add the component name itself
        const pages: string[] = [key];

        // Add each story with the format "ComponentName/StoryName"
        if (value.stories) {
          pages.push(...value.stories.map(story => `${key}/${story}`));
        }

        return pages;
      })
      .filter(page => {
        // Filter out internal-only pages if hideInternalPages is true
        if (this.hideInternalPages) {
          const componentName = page.split('/')[0];
          return !items[componentName]?.internalOnly;
        }
        return true;
      });
  }

  generateSearchItems(
    type: string,
    storyMap: readonly string[],
  ): readonly SearchItem[] {
    return storyMap
      .map(item => {
        const [component, story] = item.split('/');

        return {
          type,
          context: {
            name: story
              ? `${prettyPrint(component)}/${prettyPrint(story)}`
              : prettyPrint(item),
            link: `/${type}/${item}`,
          },
          displayValue: story
            ? `${prettyPrint(component)}/${prettyPrint(story)}`
            : prettyPrint(item),
        };
      })
      .sort((a, b) => (a.context.name < b.context.name ? -1 : 1));
  }

  private levenshteinDistance(a: string, b: string): number {
    if (a.length === 0) return b.length;
    if (b.length === 0) return a.length;

    const matrix: number[][] = new Array(b.length + 1)
      .fill(null)
      .map(() => new Array(a.length + 1).fill(null));

    for (let i = 0; i <= a.length; i++) matrix[0][i] = i;
    for (let j = 0; j <= b.length; j++) matrix[j][0] = j;

    for (let j = 1; j <= b.length; j++) {
      for (let i = 1; i <= a.length; i++) {
        const substitutionCost = a[i - 1] === b[j - 1] ? 0 : 1;
        matrix[j][i] = Math.min(
          matrix[j][i - 1] + 1, // deletion
          matrix[j - 1][i] + 1, // insertion
          matrix[j - 1][i - 1] + substitutionCost, // substitution
        );
      }
    }

    return matrix[b.length][a.length];
  }

  handleQuery(query: string): void {
    this.displaySearchResults = true;
    const queryLower = query.toLowerCase();

    // Normalize the query by replacing spaces with slashes
    const normalizedQuery = queryLower.replace(/\s+/g, '/');

    // Check if any component matches the exact query or normalized query
    const matchingComponent = this.searchItems.find(
      item =>
        item.type === 'components' &&
        (item.context.name.toLowerCase() === queryLower ||
          item.context.name.toLowerCase() === normalizedQuery),
    );

    const filteredResults = this.searchItems.filter(item => {
      const itemNameLower = item.displayValue?.toLowerCase();

      // Exclude stories of the exact matching component
      if (
        matchingComponent &&
        item.context.link.startsWith(
          `/components/${matchingComponent.context.name.toLowerCase()}/`,
        )
      ) {
        return false;
      }

      // Include items whose displayValue matches the query or normalized query
      return (
        itemNameLower?.includes(queryLower) ||
        itemNameLower?.includes(normalizedQuery)
      );
    });

    // Sort results by exact match first, then by Levenshtein distance
    this.searchResults = filteredResults.sort((a, b) => {
      const aName = a.displayValue?.toLowerCase() || '';
      const bName = b.displayValue?.toLowerCase() || '';

      // Check for exact matches first
      const aExactMatch = aName === queryLower || aName === normalizedQuery;
      const bExactMatch = bName === queryLower || bName === normalizedQuery;

      if (aExactMatch && !bExactMatch) return -1;
      if (!aExactMatch && bExactMatch) return 1;

      // If neither is an exact match, sort by Levenshtein distance
      const aDistance = this.levenshteinDistance(aName, queryLower);
      const bDistance = this.levenshteinDistance(bName, queryLower);
      return aDistance - bDistance;
    });
  }

  handleSelection(selection: SearchItem): void {
    this.displaySearchResults = false;
    this.router.navigate([selection.context.link]);
  }
}
